import { useState, useEffect } from 'react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { 
  Bell, 
  RefreshCw,
  Trash2,
  CheckCheck,
  User,
  Wallet,
  ShoppingCart,
  Clock,
  AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NOTIFICATION_ICONS = {
  new_reseller: <User className="h-5 w-5 text-green-600" />,
  new_sub_reseller: <User className="h-5 w-5 text-blue-600" />,
  wallet_request: <Wallet className="h-5 w-5 text-yellow-600" />,
  wallet_credit: <Wallet className="h-5 w-5 text-green-600" />,
  new_order: <ShoppingCart className="h-5 w-5 text-purple-600" />,
  default: <Bell className="h-5 w-5 text-slate-600" />
};

const NOTIFICATION_COLORS = {
  new_reseller: 'border-l-green-500 bg-green-50',
  new_sub_reseller: 'border-l-blue-500 bg-blue-50',
  wallet_request: 'border-l-yellow-500 bg-yellow-50',
  wallet_credit: 'border-l-green-500 bg-green-50',
  new_order: 'border-l-purple-500 bg-purple-50',
  default: 'border-l-slate-500 bg-slate-50'
};

export default function AdminNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [clearAllConfirmOpen, setClearAllConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  // Import auth context for permission checks
  const { hasPermission } = useAuth();
  const canDeleteNotifications = hasPermission('notifications:delete');

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/notifications`);
      setNotifications(response.data);
    } catch (error) {
      toast.error('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/admin/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
    } catch (error) {
      toast.error('Failed to mark notification as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/admin/notifications/mark-all-read`);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all notifications as read');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/admin/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success('Notification deleted');
      setDeleteTargetId(null);
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  const bulkDeleteNotifications = async () => {
    try {
      await Promise.all(
        selectedNotifications.map(id => axios.delete(`${API}/admin/notifications/${id}`))
      );
      setNotifications(prev => prev.filter(n => !selectedNotifications.includes(n.id)));
      toast.success(`Deleted ${selectedNotifications.length} notifications`);
      setSelectedNotifications([]);
      setDeleteConfirmOpen(false);
    } catch (error) {
      toast.error('Failed to delete notifications');
    }
  };

  const clearAllNotifications = async () => {
    try {
      await axios.delete(`${API}/admin/notifications/clear-all`);
      setNotifications([]);
      toast.success('All notifications cleared');
      setClearAllConfirmOpen(false);
    } catch (error) {
      toast.error('Failed to clear notifications');
    }
  };

  const handleSelectNotification = (id) => {
    setSelectedNotifications(prev => 
      prev.includes(id) 
        ? prev.filter(nId => nId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedNotifications.length === notifications.length) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(notifications.map(n => n.id));
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getIcon = (type) => NOTIFICATION_ICONS[type] || NOTIFICATION_ICONS.default;
  const getColor = (type) => NOTIFICATION_COLORS[type] || NOTIFICATION_COLORS.default;

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <DashboardLayout role="admin">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between  flex-wrap md-nowrap">
          <div>
            <h1 className="text-3xl font-bold text-slate-800" data-testid="notifications-title">
              Notifications
            </h1>
            <p className="text-slate-500 mt-1">
              {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
            </p>
          </div>
          <div className="flex gap-2 mt-2 md:m-0">
            <Button onClick={fetchNotifications} variant="outline" data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            {unreadCount > 0 && (
              <Button onClick={markAllAsRead} variant="outline" data-testid="mark-all-read-btn">
                <CheckCheck className="h-4 w-4 mr-2" />
                Mark All Read
              </Button>
            )}
            {canDeleteNotifications && notifications.length > 0 && (
              <Button 
                variant="destructive" 
                onClick={() => setClearAllConfirmOpen(true)}
                data-testid="clear-all-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Clear All
              </Button>
            )}
          </div>
        </div>

        {/* Summary Card */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600">Total</p>
                  <p className="text-2xl font-bold text-blue-900">{notifications.length}</p>
                </div>
                <Bell className="h-8 w-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-600">Unread</p>
                  <p className="text-2xl font-bold text-yellow-900">{unreadCount}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-yellow-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600">Read</p>
                  <p className="text-2xl font-bold text-green-900">{notifications.length - unreadCount}</p>
                </div>
                <CheckCheck className="h-8 w-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bulk Actions - Only show if user has delete permission */}
        {canDeleteNotifications && selectedNotifications.length > 0 && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-red-700">
                  {selectedNotifications.length} notification{selectedNotifications.length > 1 ? 's' : ''} selected
                </p>
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={() => setDeleteConfirmOpen(true)}
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Selected
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notifications List */}
        <Card className="bg-white border-0 shadow-sm" data-testid="notifications-list">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-16">
                <Bell className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No notifications</p>
                <p className="text-sm text-slate-400 mt-1">You're all caught up!</p>
              </div>
            ) : (
              <div>
                {/* Select All Header - Only show if user can delete */}
                {canDeleteNotifications && (
                  <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border-b">
                    <Checkbox
                      checked={selectedNotifications.length === notifications.length && notifications.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                    <span className="text-sm text-slate-600">Select All</span>
                  </div>
                )}

                {/* Notifications */}
                <div className="divide-y divide-slate-100">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`flex items-start gap-4 p-4 border-l-4 transition-colors hover:bg-slate-50 ${
                        getColor(notification.type)
                      } ${notification.is_read ? 'opacity-70' : ''}`}
                      data-testid={`notification-${notification.id}`}
                    >
                      {canDeleteNotifications && (
                        <Checkbox
                          checked={selectedNotifications.includes(notification.id)}
                          onCheckedChange={() => handleSelectNotification(notification.id)}
                        />
                      )}
                      
                      <div className="flex-shrink-0 mt-1">
                        {getIcon(notification.type)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-xs capitalize">
                            {notification.type?.replace('_', ' ')}
                          </Badge>
                          {!notification.is_read && (
                            <Badge className="bg-blue-500 text-white text-xs">New</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-800">{notification.message}</p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                          <Clock className="h-3 w-3" />
                          {formatDate(notification.created_at)}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        {!notification.is_read && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => markAsRead(notification.id)}
                            title="Mark as read"
                            data-testid={`mark-read-${notification.id}`}
                          >
                            <CheckCheck className="h-4 w-4 text-green-600" />
                          </Button>
                        )}
                        {canDeleteNotifications && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => setDeleteTargetId(notification.id)}
                            title="Delete"
                            data-testid={`delete-${notification.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Single Delete Confirmation */}
        <Dialog open={!!deleteTargetId} onOpenChange={() => setDeleteTargetId(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Notification</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this notification? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteTargetId(null)}>Cancel</Button>
              <Button variant="destructive" onClick={() => deleteNotification(deleteTargetId)}>Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Delete Confirmation */}
        <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Selected Notifications</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete {selectedNotifications.length} selected notifications? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={bulkDeleteNotifications}>Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Clear All Confirmation */}
        <Dialog open={clearAllConfirmOpen} onOpenChange={setClearAllConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Clear All Notifications</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete ALL notifications? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setClearAllConfirmOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={clearAllNotifications}>Clear All</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
